import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, Users } from 'lucide-react';
import { formatDuration } from '../../utils/formatTime';
import { formatCurrency } from '../../utils/format';
import DeltaBadge from './DeltaBadge';

function SortHeader({ label, field, sortField, sortDir, onSort, tooltip }) {
  const active = sortField === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none group"
      onClick={() => onSort(field)}
      title={tooltip}
    >
      <div className="inline-flex items-center gap-1">
        {label}
        <span className={`transition-colors ${active ? 'text-orange-500' : 'text-gray-300 group-hover:text-gray-400'}`}>
          {active && sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </div>
    </th>
  );
}

// Decimal hours (as the backend sends them) -> "Xh Ym", reusing the same formatter Scheduler's
// month overview uses, rather than re-deriving hour/minute math here.
function hoursDisplay(decimalHours) {
  return formatDuration(Math.round((decimalHours ?? 0) * 60));
}

function sortValue(row, field) {
  switch (field) {
    case 'name': return row.name ?? '';
    case 'scheduledHours': return row.scheduledHours.thisMonth;
    case 'overtimeHours': return row.overtimeHours;
    case 'actualHours': return row.actualHours;
    case 'presentDays': return row.presentDays;
    case 'labourCostPaise': return row.labourCostPaise.thisMonth;
    case 'noShows': return row.noShows;
    case 'lateArrivals': return row.lateArrivals;
    case 'leaveDays': return row.leaveDays;
    default: return '';
  }
}

function StaffScorecardTable({ rows = [], loading, error }) {
  const { t } = useTranslation('reports');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(
    () =>
      [...(rows ?? [])].sort((a, b) => {
        const av = sortValue(a, sortField);
        const bv = sortValue(b, sortField);
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      }),
    [rows, sortField, sortDir]
  );

  const sortProps = { sortField, sortDir, onSort: handleSort };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="px-4 pt-4">
        <h2 className="text-sm font-semibold text-gray-800">{t('staffScorecard.heading')}</h2>
      </div>

      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Users size={36} className="text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-600">{t('staffScorecard.empty')}</p>
        </div>
      )}

      {!error && (loading || rows.length > 0) && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-y border-gray-200">
              <tr>
                <SortHeader label={t('staffScorecard.columns.name')} field="name" {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.scheduledHours')} field="scheduledHours" {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.overtimeHours')} field="overtimeHours" tooltip={t('staffScorecard.columns.overtimeTooltip')} {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.actualHours')} field="actualHours" {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.presentDays')} field="presentDays" tooltip={t('staffScorecard.columns.presentDaysTooltip')} {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.labourCost')} field="labourCostPaise" {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.noShows')} field="noShows" {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.lateArrivals')} field="lateArrivals" {...sortProps} />
                <SortHeader label={t('staffScorecard.columns.leaveDays')} field="leaveDays" {...sortProps} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && [...Array(3)].map((_, i) => (
                <tr key={i}>
                  <td colSpan={9} className="px-4 py-3">
                    <div className="h-3 rounded bg-gray-100 animate-pulse" />
                  </td>
                </tr>
              ))}
              {!loading && sorted.map(row => (
                <tr key={row.employeeId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <span className="inline-flex items-center gap-2">
                      {row.name || '—'}
                      {!row.isActive && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          {t('staffScorecard.departedBadge')}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div className="flex items-center gap-2">
                      <span>{hoursDisplay(row.scheduledHours.thisMonth)}</span>
                      <DeltaBadge metric={row.scheduledHours} coloring="neutral" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{hoursDisplay(row.overtimeHours)}</td>
                  <td className="px-4 py-3 text-gray-700">{hoursDisplay(row.actualHours)}</td>
                  <td className="px-4 py-3 text-gray-700">{row.presentDays}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <div className="flex items-center gap-2">
                      <span>{formatCurrency(row.labourCostPaise.thisMonth / 100, { decimals: 2 })}</span>
                      <DeltaBadge metric={row.labourCostPaise} coloring="neutral" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{row.noShows}</td>
                  <td className="px-4 py-3 text-gray-700">{row.lateArrivals}</td>
                  <td className="px-4 py-3 text-gray-700">{row.leaveDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default StaffScorecardTable;
